import React, { useEffect, useState } from "react";
import { Modal, TextField, Autocomplete } from "@mui/material";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";
import { FormatNumeroInput, LimpiarNumero } from "../../../Helpers/FormatNumberInput";

const SERVICIO_VACIO = {
  idServicio: 0,
  idLineaVenta: "",
  nombreServicio: "",
  descripcion: "",
  precioLista: "",
  descuento: 0,
  impuesto: 0,
};

// Modal de alta/edición del Catálogo de Servicio — idServicio null/0 = alta, con valor = edición.
// ObtenerServicioID trae tanto los datos del servicio (si existe) como el catálogo de líneas
// de venta (LineasVenta) — se llama siempre para tener ese combo disponible. Guarda con
// CotizacionesCRM/Guardar.
export const CatalogoServicioModal = ({ open, onClose, idServicio, onGuardado }) => {
  const [form, setForm] = useState(SERVICIO_VACIO);
  const [lineasVenta, setLineasVenta] = useState([]);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  useEffect(() => {
    if (!open) return;

    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .get(`CotizacionesCRM/ObtenerServicioID?idServicio=${idServicio || 0}`)
      .then((result) => {
        setLineasVenta(Array.isArray(result?.lineasVenta) ? result.lineasVenta : []);

        const servicio = result?.servicio;
        setForm(
          idServicio && servicio
            ? {
                idServicio: servicio.id_servicio ?? idServicio,
                idLineaVenta: servicio.id_linea_venta ?? "",
                nombreServicio: servicio.nombre_servicio ?? "",
                descripcion: servicio.descripcion ?? "",
                precioLista: servicio.precio_lista ?? "",
                descuento: servicio.descuento ?? 0,
                impuesto: servicio.impuesto ?? 0,
              }
            : SERVICIO_VACIO
        );
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("CATÁLOGO DE SERVICIO", JSON.stringify(error), "error");
      });
  }, [open, idServicio]);

  const onChangeForm = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const onChangeNumero = (campo, valor) => {
    const limpio = LimpiarNumero(valor);
    if (limpio !== "" && !/^-?\d*\.?\d*$/.test(limpio)) return;
    onChangeForm(campo, limpio);
  };

  const handleGuardar = () => {
    if (!form.nombreServicio) {
      Swal.fire("CATÁLOGO DE SERVICIO", "Favor de capturar el nombre del servicio", "warning");
      return;
    }
    if (!form.precioLista || Number(form.precioLista) <= 0) {
      Swal.fire("CATÁLOGO DE SERVICIO", "Favor de capturar un precio de lista válido", "warning");
      return;
    }

    const post = {
      IdServicio: form.idServicio || 0,
      IdLineaVenta: Number(form.idLineaVenta) || 0,
      NombreServicio: form.nombreServicio,
      Descripcion: form.descripcion,
      PrecioLista: Number(form.precioLista),
      Descuento: Number(form.descuento) || 0,
      Impuesto: Number(form.impuesto) || 0,
    };

    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .post("CotizacionesCRM/Guardar", post)
      .then(() => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("CATÁLOGO DE SERVICIO", "Registro guardado con éxito", "success");
        onGuardado();
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("CATÁLOGO DE SERVICIO", JSON.stringify(error), "error");
      });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="crmcs-catalogo-modal">
        <LoaderScreen loader={loader} />
        <div className="crmcs-catalogo-modal-close" onClick={onClose}>X</div>
        <PlantillaScreen
          titulo={idServicio ? "EDITAR SERVICIO" : "AGREGAR SERVICIO"}
          className_main={1}
          className="crmcs-catalogo-modal-plantilla"
        >
          <div className="crmcs-catalogo-modal-body">
            <TextField
              size="small"
              label="Nombre del servicio"
              value={form.nombreServicio}
              onChange={(e) => onChangeForm("nombreServicio", e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Descripción"
              value={form.descripcion}
              onChange={(e) => onChangeForm("descripcion", e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Autocomplete
              size="small"
              options={lineasVenta}
              getOptionLabel={(o) => o.departamento || ""}
              isOptionEqualToValue={(o, v) => o.idDepartamento === v.idDepartamento}
              value={lineasVenta.find((l) => l.idDepartamento === form.idLineaVenta) || null}
              onChange={(_, v) => onChangeForm("idLineaVenta", v ? v.idDepartamento : "")}
              renderInput={(params) => <TextField {...params} label="Línea de venta" />}
              fullWidth
            />
            <TextField
              size="small"
              type="text"
              inputMode="decimal"
              label="Precio de lista"
              value={FormatNumeroInput(form.precioLista)}
              onChange={(e) => onChangeNumero("precioLista", e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              type="text"
              inputMode="decimal"
              label="Descuento"
              value={FormatNumeroInput(form.descuento)}
              onChange={(e) => onChangeNumero("descuento", e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              type="text"
              inputMode="decimal"
              label="Impuesto"
              value={FormatNumeroInput(form.impuesto)}
              onChange={(e) => onChangeNumero("impuesto", e.target.value)}
              fullWidth
            />
          </div>

          <div className="crmcs-catalogo-modal-buttons">
            <button className="crmcs-btn-agregar" onClick={handleGuardar}>Guardar</button>
          </div>
        </PlantillaScreen>
      </div>
    </Modal>
  );
};
